import { Button, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { useQuestionnaireListContext } from "../context/useQuestionnaireList";
import { useTranslation } from "react-i18next";
import { SsqSsqHeading } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { HeadingsSectionData } from "../QuestionnairesList";
import { Badge, FoodsIcon, LinearProgress } from "@foodpilot/foods";
import { Link } from "react-router";

export const GridView = () => {
    const { ssqSsqHeadings, post, isPostLoading } = useQuestionnaireListContext();

    const headingsData: HeadingsSectionData[] = ssqSsqHeadings.map((ssqSsqHeading: SsqSsqHeading) => {
        return {
            ssqSsqHeading: ssqSsqHeading,
            completion: !isPostLoading ? (post?.completions.headings[ssqSsqHeading.id]?.result ?? null) : undefined,
        };
    });

    return (
        <Stack width={"100%"}>
            <Grid2 container spacing={2} columns={{ xl: 12, md: 8, xs: 4 }}>
                {headingsData.map((heading, index) => {
                    return <GridBlock key={`heading-list-grid-${index}`} heading={heading} blockNumber={index} />;
                })}
            </Grid2>
        </Stack>
    );
};

type GridBlockProps = {
    heading: HeadingsSectionData;
    blockNumber: number;
};
const GridBlock = (props: GridBlockProps) => {
    const { heading, blockNumber } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const { canAnswerSsq, canViewSsq, isLocked, completeQuestionnaireUrl } = useQuestionnaireListContext();

    const isCompleted = heading.completion === 100;
    const goToQuestionnaireText =
        !canAnswerSsq || isLocked ? t("questionnaire.consult")
        : isCompleted ? t("questionnaire.modify")
        : t("questionnaire.complete");

    const url = completeQuestionnaireUrl(heading.ssqSsqHeading);

    return (
        <Grid2 size={4} border={`1px solid ${theme.custom.grey[600]}`} borderRadius={theme.spacing(1.5)}>
            <Stack width={"100%"} height={"100%"} padding={theme.spacing(3)} gap={theme.spacing(4)}>
                <Stack gap={theme.spacing(1)} alignItems={"start"} flex={1}>
                    <Badge>{blockNumber + 1}</Badge>

                    <Stack gap="4px">
                        <Typography variant="h3">{heading.ssqSsqHeading.ssqHeading.title}</Typography>
                    </Stack>
                </Stack>
                <Stack gap={theme.spacing(2)}>
                    <Stack>
                        <Stack flexDirection="row" gap={theme.spacing(0.5)} flexWrap={"wrap"}>
                            <Typography
                                variant="big-bold"
                                color={heading.completion === 100 ? theme.custom.green[400] : undefined}
                            >
                                {t("global.number.percent", "{{value}}%", {
                                    value: heading.completion,
                                })}
                            </Typography>
                            <Typography variant="body">
                                {t("questionnaire.completion.of_data_entered", "of data entered")}
                            </Typography>
                        </Stack>
                        <LinearProgress value={heading.completion ?? 0} hideValueLabel />
                    </Stack>
                    {canViewSsq && (
                        <Link to={url}>
                            <Button
                                variant="secondary"
                                endIcon={<FoodsIcon size={2} icon={"arrowRight"} />}
                                sx={{
                                    // Link seems to override the color that is not properly set in theme
                                    // specifically for this variant
                                    justifyContent: "space-between",
                                    color: theme.custom.grey[2800],
                                    width: "100%",
                                }}
                            >
                                {goToQuestionnaireText}
                            </Button>
                        </Link>
                    )}
                </Stack>
            </Stack>
        </Grid2>
    );
};
